import storageFactory from './StorageFactory';
import StorageMigration from './StorageMigration';
import LocalStorageAdapter from './LocalStorageAdapter';

/**
 * Storage Service
 * Main entry point for storage operations
 * Handles initialization, migration, and provides storage instance
 */
class StorageService {
    constructor() {
        this.storage = null;
        this.initialized = false;
    }

    /**
     * Initialize storage with configuration
     * @param {Object} config - Storage configuration
     * @returns {Promise<IStorage>}
     */
    async initialize(config = {}) {
        if (this.initialized) {
            return this.storage;
        }

        try {
            // Create storage instance
            this.storage = await storageFactory.createStorage(config);

            // Run migration if using IndexedDB
            if (config.USE_INDEXDB && this.storage.constructor.name === 'IndexedDBStorage') {
                await this.runMigration();
            }

            this.initialized = true;
            console.log('✅ Storage service initialized');

            return this.storage;
        } catch (error) {
            console.error('❌ Failed to initialize storage service:', error);
            throw error;
        }
    }

    /**
     * Run migration from localStorage to IndexedDB
     * @private
     */
    async runMigration() {
        try {
            const localStorageAdapter = new LocalStorageAdapter();
            const migration = new StorageMigration(localStorageAdapter, this.storage);

            // Check if migration needed
            if (await migration.isMigrationCompleted()) {
                console.log('ℹ️ Migration already completed');
                return;
            }

            // Run migration (don't clear localStorage by default for safety)
            const result = await migration.migrate(false);

            if (result.success) {
                console.log('✅ Migration successful');
            } else {
                console.warn('⚠️ Migration completed with errors:', result.errors);
            }

            // Optionally verify migration
            if (process.env.NODE_ENV === 'development') {
                const verification = await migration.verify();
                if (!verification.success) {
                    console.warn('⚠️ Migration verification found mismatches:', verification.mismatches);
                }
            }
        } catch (error) {
            console.error('❌ Migration failed:', error);
            // Don't throw - allow app to continue with current storage
        }
    }

    /**
     * Get storage instance
     * @returns {IStorage}
     */
    getStorage() {
        if (!this.initialized || !this.storage) {
            throw new Error('Storage service not initialized. Call initialize() first.');
        }
        return this.storage;
    }

    /**
     * Reset storage service (useful for testing)
     */
    reset() {
        this.storage = null;
        this.initialized = false;
        storageFactory.reset();
    }
}

// Export singleton instance
const storageService = new StorageService();

export default storageService;
export { StorageService };
